import React, { useEffect, useMemo, useState } from 'react';

import { MINIMUM_PAGE_COUNT } from 'constants/page';

import useQuery from 'hooks/useQuery';
import useTitle from 'hooks/useTitle';
import { useDesignationsQuery } from 'hooks/query/useLookupQueries';
import { useFetchSkillsV1Query } from 'hooks/query/useSkillQueries';
import { useExportResourceMutation, useFetchResourceQuery } from 'hooks/query/useUserQueries';
import { handleError } from 'utils/errorHandler';

import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';
import { ResourceFilterV1, ResourceResultsV1 } from './components';

import type { QueryObject, RouterProps } from 'interface/queryParam';

import type { DesignationRecord, ResourceEmployeeV1 } from './types';

const PAGE_COUNT = 10;

type ResourceResponse = {
  data: ResourceEmployeeV1[];
  meta: { total: number };
};

type SkillListResponse = {
  data: DesignationRecord[];
};

const ResourceFinderV1: React.FC<RouterProps> = (props) => {
  const [query, setQuery] = useQuery(props) as [QueryObject, (next: QueryObject) => void];
  const [exporting, setExporting] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_COUNT);

  const setTitle = useTitle();

  useEffect(() => {
    setTitle('People Finder');
  }, [setTitle]);

  const { page, ...filterQuery } = query;

  useEffect(() => {
    if (!page) {
      setQuery({ ...query, page: 1 });
    }
  }, [page, query, setQuery]);

  const { data: skillsResponse, isLoading: loadingSkills } = useFetchSkillsV1Query<SkillListResponse>();
  const { data: designations = [], isLoading: loadingDesignations } = useDesignationsQuery();

  const skills = useMemo(() => (Array.isArray(skillsResponse?.data) ? skillsResponse.data : []), [skillsResponse]);

  const {
    data: resourceResponse,
    isLoading: loadingEmployees,
    isError: isResourceError,
  } = useFetchResourceQuery<ResourceResponse>(query, Boolean(page));

  const { mutateAsync: exportResource } = useExportResourceMutation();

  useEffect(() => {
    if (isResourceError) {
      handleError(new Error('Failed to load resource finder results'));
    }
  }, [isResourceError]);

  const employees = resourceResponse?.data ?? [];
  const total = resourceResponse?.meta?.total ?? 0;
  const pageCount = Math.ceil(total / pageSize);

  const handlePageChange = (nextPage: number) => {
    setQuery({ ...query, page: nextPage });
  };

  const handleFilterClick = (newFilterState: QueryObject) => {
    setQuery({ ...query, ...newFilterState, page: 1 });
  };

  const handleExportClick = async () => {
    setExporting(true);

    try {
      const exportResponse = await exportResource(query);
      const link = document.createElement('a');

      link.href = URL.createObjectURL(exportResponse.data);
      link.download = 'leapfroggers skills.csv';
      link.click();

      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } finally {
      setExporting(false);
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setQuery({ ...query, size: nextPageSize, page: 1 });
  };

  const handleClearClick = () => {};

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">People Finder</h3>
            </div>
          </div>
          <div className="full-scope-card__content d-flex position-relative">
            <div className="resource-filter-area">
              <ResourceFilterV1
                skills={skills}
                initialState={filterQuery}
                designations={designations as DesignationRecord[]}
                onFilter={handleFilterClick}
                onClear={handleClearClick}
                loadingSkills={loadingSkills}
                loadingDesignations={loadingDesignations}
              />
            </div>
            <div className="resource-results-area">
              {loadingEmployees ? (
                <Loading />
              ) : (
                <>
                  <ResourceResultsV1
                    employees={employees}
                    total={total}
                    exporting={exporting}
                    onExport={handleExportClick}
                  />
                  <Pagination
                    pageData={{ page: +String(page ?? '') }}
                    onPageChange={handlePageChange}
                    pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResourceFinderV1;
